import React from 'react';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../../i18n/languages';
import { useAuth } from '../../context/AuthContext';
import { UserService } from '../../services/user.service';

/**
 * Picks the app language. The choice takes effect at once and is kept on this device; for a signed-in
 * user it is also saved on their account, so their next sign-in anywhere opens in the same language.
 */
const LanguageSwitcher: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();

    const change = async (e: SelectChangeEvent) => {
        const code = e.target.value;
        await i18n.changeLanguage(code);
        if (user) {
            // The switch has already happened; failing to remember it on the account is not worth an error.
            UserService.updateLanguage(code).catch(() => undefined);
        }
    };

    return (
        <Select
            size="small"
            variant="standard"
            disableUnderline
            value={i18n.resolvedLanguage ?? i18n.language}
            onChange={change}
            inputProps={{ 'aria-label': t('language.label') }}
            startAdornment={<TranslateIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />}
            sx={{ fontSize: '0.875rem', mr: 1 }}
        >
            {LANGUAGES.map(l => (
                <MenuItem key={l.code} value={l.code} lang={l.code}>{l.label}</MenuItem>
            ))}
        </Select>
    );
};

export default LanguageSwitcher;
